import React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import memoizeOne from 'memoize-one'
import { 
  Map, 
  MapStyle, 
  ReduxMapOptions,
  MapControl
} from '@leverege/ui-mapbox-elements'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import { Layer, Source } from 'react-mapbox-gl'
import { Content, TabPane, Flex, Text, Pane, Button } from '@leverege/ui-elements'
import { UI } from '@leverege/ui-redux'

import { BreadcrumbActions, NotifyActions } from '~/actions'
import { SplitPane } from '~/views'
import ImageController from './ImageController'
import { Geotile as GeotileUtils } from '../../utils'
import { GeotileFactory } from '../../editors/geotile'
import style from './style.css'
import { GeotileActions } from '../../actions'

const MAP_NAME = 'map'
const MAPBOX_APIKEY = process.env.MAPBOX_APIKEY

class GeotileHomeScreen extends React.Component {
  static getDerivedStateFromProps( props, state ) {
    const activeTab = props.location.pathname.split( '/' )[5]
    const validTabs = [ 'info', 'tile' ]
    if ( activeTab && validTabs.includes( activeTab ) && state.activeTab !== activeTab ) {
      return { activeTab }
    }
    return null
  }

  constructor( props ) {
    super( props )
    const { dispatch } = props
    this.state = {
      image : null,
      mode : 'select',
      activeTab : 'info',
      imageLoading : false
    }
    this.mapOptions = new ReduxMapOptions( this, MAP_NAME, { UI }, dispatch )
    this.refreshImageTimer = null
    this.sourceImage = new Image()
    this.sourceImage.onload = this.imgOnLoad
    this.sourceImage.onerror = this.imgOnError
    this.ic = new ImageController( { 
      id : 'ic', 
      updateFeature : this.onUpdateFeature,
      onReposition : this.onReposition,
      getFeature : this.getFeature, 
      mapSource : this
    } )

    this.geocoder = new MapboxGeocoder( { accessToken : MAPBOX_APIKEY } )
  }

  componentDidMount() {
    this.updateBreadcrumbs()
    this.fetchGeotileData()
    window.addEventListener( 'keydown', this.onKeyDown )
  }

  componentWillUnmount() {
    window.removeEventListener( 'keydown', this.onKeyDown )
    clearInterval( this.timer )
    clearTimeout( this.refreshImageTimer )
  }

  componentDidUpdate( prevProps ) {
    const { geotile } = this.props
    this.checkForUpdates( prevProps )
    if ( geotile?.metadata?.center && !prevProps.geotile?.metadata?.center ) {
      this.flyToCenter( geotile.metadata )
    }
    if ( geotile.name && !prevProps.geotile.name ) {
      this.updateBreadcrumbs()
    }
    if ( geotile.sourceImagePath !== prevProps.geotile.sourceImagePath ) {
      this.updateImage( geotile.sourceImagePath )
    }

    if ( geotile?.metadata?.rotationAngle && !prevProps.geotile?.metadata ) {
      this.ic.rotation = geotile?.metadata?.rotationAngle
    }
  }

  checkForUpdates( prevProps ) {
    const { geotile, dispatch } = this.props
    if ( geotile.isGenerating && !this.timer ) {
      this.timer = setInterval( this.fetchGeotileData, 10 * 1000 )
    } else if ( !geotile.isGenerating && prevProps.geotile.isGenerating ) {
      clearInterval( this.timer )
      dispatch( NotifyActions.success( 'Tiles generated' ) )
    }
  }
  
  getFeature = () => this.props.feature

  updateImage = ( url ) => {
    this.setState( { imageLoading : true } )
    this.sourceImage.src = url
  }

  imgOnLoad = () => {
    this.setState( { imageUrl : this.sourceImage.src, imageLoading : false } )
    this.ic.setUrl( this.sourceImage.src, this.sourceImage.src.split( '/' ).slice( -1 )[0] )
  }

  imgOnError = () => {
    const { geotile } = this.props
    const url = `${geotile?.sourceImagePath}?hash=${Date.now()}`
    this.refreshImageTimer = setTimeout( () => this.updateImage( url ), 1000 )
  }

  updateBreadcrumbs() {
    const { dispatch, geotile, geotileId, projectId } = this.props
    dispatch( BreadcrumbActions.setBreadcrumbs( [ 
      { name : 'Geotiles', link : `/project/${projectId}/geotile` }, 
      { name : geotile.name || geotileId } ] ) )
  }

  fetchGeotileData = () => {
    const { dispatch, geotileId, projectId } = this.props
    dispatch( GeotileActions.getGeotile( { projectId, geotileId } ) )
  }

  onKeyDown = ( e ) => {
    if ( e.keyCode === 27 ) { this.onModeChange( 'simple_select' ) }
  }

  onTabClicked = ( event ) => { 
    const { history, projectId, geotileId } = this.props
    history.push( `/project/${projectId}/geotile/${geotileId}/${event.value}` )
    this.setState( { activeTab : event.value } )
  }

  onUpdateFeature = ( currentFeature ) => {
    const { dispatch } = this.props
    dispatch( GeotileActions.updateEditor( { currentFeature } ) )
  }

  onZoom = ( what, evt ) => {
    const { dispatch } = this.props
    dispatch( GeotileActions.updateEditor( { currentZoom : this.map?.getZoom() || 0 } ) )
  }

  onModeChange = ( mode ) => {
    const { dispatch } = this.props
    this.setState( { mode } )
    dispatch( GeotileActions.updateEditor( { drawMode : mode } ) )
  }

  flyToCenter = ( { center, maxzoom } ) => {
    const [ lon, lat ] = center
    MapControl.flyTo( MAP_NAME, { lon, lat, duration : 0.5, zoom : Math.min( maxzoom, 16 ) } )
  }

  onMapReady = ( map ) => {
    const { geotile } = this.props
    this.map = map
    this.mapOptions.onMapReady( map )
    if ( geotile?.metadata?.center ) {
      this.flyToCenter( geotile.metadata )
    }
    this.geocoder.addTo( `#${style.geocoder}` )
    this.geocoder.on( 'result', ( { result } ) => {
      this.flyToCenter( result )
    } )
  }

  onReposition = ( imageHash ) => { this.setState( { imageHash } ) }

  renderTiles = memoizeOne( ( geotilePath, isGenerating, opacity = 1 ) => {
    if ( !geotilePath || isGenerating ) return null
    const { geotile } = this.props
    const src = GeotileUtils.getLayerData( geotile )
    if ( !src ) return null
    
    const tileSource = (
      <Source 
        id="tileSource"
        key="tileSource"
        tileJsonSource={src} 
        onSourceAdded={this.onSourceAdded} /> 
    )

    const tileLayer = (
      <Layer 
        id="tileSource-image"
        type="raster" 
        sourceId="tileSource" 
        paint={{ 'raster-opacity' : opacity }}
        key="tileSource-image" />
    )
    return [ tileSource, tileLayer ]
  } )

  getMapAndFeatureData = () => {
    const { feature } = this.props
    const zoom = this.map.getZoom()
    return { zoom, feature }
  }

  createEditor = () => {
    const { geotile, projectId } = this.props
    const { activeTab, imageUrl, imageLoading, currentZoom } = this.state
    if ( activeTab === 'tile' ) {
      return GeotileFactory.createEditor( { 
        geotile, 
        props : { 
          projectId,
          currentZoom,
          getMapAndFeatureData : this.getMapAndFeatureData,
          setDrawMode : this.onModeChange,
          onDeleteShape : this.ic.onTrash,
          imageLoading,
          imageUrl
        },
        type : 'tile'
      } )
    }
    return GeotileFactory.createEditor( { geotile, props : {}, type : 'info' } )
  }
  
  render() {
    const { mapStyle, initialPosition, geotile, feature } = this.props
    const { editor : { tileLayerOpacity } } = geotile
    const { activeTab, imageHash } = this.state
    if ( !geotile ) {
      const cn = classnames( 'fa fa-spin fa-refresh fa-fw' )
      return (
        <Flex justify="center" align="center" className={style.loader}>
          <Flex direction="column">
            <Text>Loading geotile data...</Text>
            <i className={cn} />
          </Flex>
        </Flex>
      )
    }
    const editor = this.createEditor()
    const layers = []
    if ( this.ic && typeof this.ic.getLayers === 'function' ) {
      layers.push( this.ic.getLayers( geotile, imageHash ) )
    }

    layers.push( this.renderTiles( geotile?.tilePath, geotile?.isGenerating, tileLayerOpacity ) )
    return (
      <Content>
        <Content.Header>
          <TabPane activeTab={activeTab} onTabClicked={this.onTabClicked}>
            <TabPane.Tab tabName="Info" tabId="info"/>                
            <TabPane.Tab tabName="Tile" tabId="tile"/>                
          </TabPane>
        </Content.Header>
        <Content.Area>
          { activeTab === 'info' && editor }
          { activeTab === 'tile' && (
            <SplitPane
              className={style.mapSplit}
              left={(
                <div className={style.mapSplitLeft}>
                  <div id={style.geocoder} />
                  <Map
                    containerStyle={{ height : '100%', width : '100%' }}
                    logoPosition="top-left"
                    mapName={MAP_NAME}
                    onZoom={this.onZoom}
                    mapOptions={this.mapOptions}
                    mapStyle={MapStyle.url( mapStyle )}
                    initialPosition={initialPosition}
                    layers={layers}
                    onClick={this.onMapClick}
                    onRollover={this.onRollover}
                    onMapReady={this.onMapReady}/>
                  <Pane className={style.mapStyle} variant="mapStyle">
                    <Button variant="mapControl" onClick={this.mapOptions.onNextMapStyle}><i className="lv lv-map-view" /></Button>
                  </Pane>
                </div>
              )}
              right={(
                <div className={style.mapSplitRight}>
                  {editor}
                </div>
              )} />
          )}
        </Content.Area>
      </Content>
    )
  }
}

export default connect( ( state, props ) => {
  const { match : { params : { geotileId, projectId } } } = props
  return {
    projectId,
    geotileId,
    mapStyle : ReduxMapOptions.getMapStyle( state, MAP_NAME ),
    initialPosition : ReduxMapOptions.getInitialPosition( state, MAP_NAME ),
    geotile : state.geotile || {},
    feature : state.geotile?.editor?.currentFeature
  }
} )( GeotileHomeScreen )
